import toast from "react-hot-toast";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { FieldValues } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";

import { Customer } from "../../../types";
import { INSTRUCTION_TYPE } from "../../../Common/Constants";
import DocumentManagementForm from "../../Document-Management/form/DocumentManagementForm";
import { getParsedLocalStorage } from "../../../Utils/storage-utils";
import { extractUserDataFromPermission } from "../../../Utils/Utils";
import { userIdSelector } from "../../../features/authentication/auth-slice";
import {
  DocumentFieldType,
  DocumentFormFieldType,
  DocumentManagementFormType,
} from "../../../types/document-management/document-management-types";
import document_management_apis, { UploadDocumentPayload } from "../../../Services/document-management-apis";

const DEFAULT_FILTER_VALUES: FieldValues = {
  document_name: "",
  remarks: "",
  document_type: null,
  section: null,
  station: [],
  files: [],
};
interface AddDocumentModalProps {
  handleClose: () => void;
  customer?: Customer;
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ handleClose, customer }) => {
  const [uploadDocuments] = document_management_apis.useUploadDocumentMutation();
  const [uploadAttachments] = document_management_apis.useUploadDocumentAttachmentMutation();
  const user_id: string = useSelector(userIdSelector);

  async function handleFormValueChange(form_values: DocumentManagementFormType & { section: string[] }) {
    const { document_name, document_type, remarks: remark, files, station, section } = form_values;
    const params: UploadDocumentPayload = {
      params: { module: "LATEST_INSTRUCTION_GUIDELINE" },
      body: {
        customer_ids: getStations(section).map((stn) => stn.customerId),
        document_name,
        remark,
        document_type,
        user_id,
        issued_by: null,
        published_dt: null,
      },
    };
    const toast_id = toast.loading("Uploading...");
    try {
      const uploaded_document = await uploadDocuments(params).unwrap();
      const form_data = new FormData();

      files.map((file) => form_data.append("files", file));

      const _attachmentParams = {
        id: uploaded_document.id,
        user_id,
        files: form_data,
      };

      try {
        await uploadAttachments(_attachmentParams).unwrap();
        toast.success("Uploaded Successfully");
      } catch (error: any) {
        // console.error("UPLOAD_DOCUMENT_ATTACHMENTS", error);
        if (error.data) {
          throw new Error(error.data);
        }
        throw new Error("Error Uploading Attachments With Document");
        // toast.error("Error Uploading Attachments With Document");
      } finally {
        handleClose();
      }
    } catch (error: any) {
      // console.error("UPLOAD_DOCUMENT", error);
      if (error?.data) {
        toast.error(error.data);
        return;
      }

      if (error.error && typeof error.error === "string") {
        toast.error(error.error);
        return;
      }

      toast.error("Error Uploading Document");
    } finally {
      toast.dismiss(toast_id);
    }
  }

  const getStations = (sections: string[]) => {
    if (!sections) return [];
    if (!Array.isArray(sections)) {
      return [];
    }
    const allOptions: any[] = [];
    sections?.forEach((section) => {
      const options = user_permissions.sectionToCustomersMap[section];
      if (options) allOptions.push(...options);
    });
    return allOptions;
  };

  const user_permissions = useMemo(() => {
    const permissions = getParsedLocalStorage("permissions");
    const _permissions = extractUserDataFromPermission(permissions);

    const {
      innerKeysList: sectionList,
      customerList,
      innerToCustomerNames,
      innerToCustomers,
      outerToInnerKeys,
    } = _permissions;
    return {
      sectionList: sectionList as string[],
      customerList: customerList as Customer[],
      sectionToCustomerNameMap: innerToCustomerNames as Record<string, string[]>,
      sectionToCustomersMap: innerToCustomers as Record<string, Customer[]>,
      divisionToSectionMap: outerToInnerKeys as Record<string, string[]>,
    };
  }, []);

  const DEFAULT_VALUES = useMemo(() => {
    if (!customer) return DEFAULT_FILTER_VALUES;
    for (const section in user_permissions.sectionToCustomerNameMap) {
      const customer_list = user_permissions.sectionToCustomerNameMap[section];

      if (customer_list.includes(customer?.customerName)) {
        return {
          ...DEFAULT_FILTER_VALUES,
          section,
          station: user_permissions.customerList.filter((item) => item.customerId === customer.customerId),
        };
      }
    }

    return DEFAULT_FILTER_VALUES;
  }, [user_permissions, customer]);

  const fields: DocumentFormFieldType[] = [
    {
      type: DocumentFieldType.Textfield,
      label: "Instruction Name",
      id: "document_name",
      required: "Field is required",
    },
    {
      type: DocumentFieldType.Dropdown,
      label: "Instruction Type",
      id: "document_type",
      required: "Field is required",
      multiple: false,
      options: INSTRUCTION_TYPE,
    },
    {
      type: DocumentFieldType.REACTIVE_DROPDOWN,
      label: "Division",
      id: "section",
      multiple: true,
      // options: user_permissions.sectionList,
      reactive_key: "division",
      required: "Field is required",
      disabled: Boolean(customer),
      get_options: () => {
        return Object.values(user_permissions.divisionToSectionMap).flat();
      },
    },
    { type: DocumentFieldType.URL, label: "Url", id: "remarks", required: false },
    { type: DocumentFieldType.Attachment, id: "files", required: true },
  ];

  // console.log("customer --> ", customer);
  // console.log("user_permissions --> ", user_permissions);
  // console.log("DEFAULT_VALUES --> ", DEFAULT_VALUES);

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        Upload New Document
        <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleClose} />
      </DialogTitle>
      <DialogContent>
        <DocumentManagementForm
          defaultValues={DEFAULT_VALUES}
          formFields={fields}
          handleFormSubmit={handleFormValueChange}
          className="upload-documents-form flex gap-4 items-center flex-wrap p-2"
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddDocumentModal;

// {"customer_id":"50bc6230-3efb-11ed-818c-150801d2970c","document_name":"test doc","remark":null,"document_type":"Technical specs","user_id":"73965d62-de33-4299-8021-4a22505486b9"}
